import { useState } from "react";
import { BadgeCheck, Link2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { imgSrc, memberSlug, focusStyle } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const ProfileDialog = ({ person, onClose, testidPrefix = "person", linkBase }) => {
  const { t, lang } = useSite();
  return (
    <Dialog open={!!person} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={`${testidPrefix}-profile-dialog`} className="rounded-3xl sm:max-w-lg p-0 overflow-hidden border-slate-100">
        {person && (
          <div>
            <div className="relative aspect-[4/3] bg-sky-100">
              {imgSrc(person.avatar) && (
                <img src={imgSrc(person.avatar)} alt={t(person, "name")} style={focusStyle(person)} className="absolute inset-0 w-full h-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
            </div>
            <div className="p-8 pt-2">
              <DialogTitle className="font-display font-extrabold text-2xl text-slate-900">
                {t(person, "name")}
              </DialogTitle>
              <DialogDescription asChild>
                <p className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-orange-50 border border-orange-100 px-3 py-1 text-xs font-bold text-orange-600">
                  <BadgeCheck size={13} />
                  {t(person, "role")}
                </p>
              </DialogDescription>
              {(t(person, "bio") || "").split("\n").filter(Boolean).map((para, i) => (
                <p key={i} data-testid={`${testidPrefix}-bio-para-${i}`} className="mt-4 text-sm sm:text-base leading-relaxed text-slate-600">
                  {para}
                </p>
              ))}
              {(person.join_url || person.policy_url || linkBase) && (
                <div className="mt-6 flex flex-wrap gap-2.5">
                  {person.join_url && (
                    <a data-testid={`${testidPrefix}-join-btn`} href={person.join_url} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-2.5 text-xs font-bold text-white hover:bg-orange-600 transition-colors duration-300">
                      {lang === "zh" ? "我想加入" : "Join Us"}
                    </a>
                  )}
                  {person.policy_url && (
                    <a data-testid={`${testidPrefix}-policy-btn`} href={person.policy_url} target="_blank" rel="noopener noreferrer"
                      className="flex-1 inline-flex items-center justify-center rounded-full border-2 border-sky-400 px-4 py-2 text-xs font-bold text-sky-500 hover:bg-sky-400 hover:text-white transition-colors duration-300">
                      {lang === "zh" ? "了解保单" : "View Plans"}
                    </a>
                  )}
                  {linkBase && (
                    <button
                      data-testid={`${testidPrefix}-copy-link`}
                      onClick={async () => {
                        try {
                          await navigator.clipboard.writeText(`${window.location.origin}${linkBase}/${memberSlug(person)}`);
                          toast.success(lang === "zh" ? "专属链接已复制" : "Link copied");
                        } catch {
                          toast.error(lang === "zh" ? "复制失败" : "Copy failed");
                        }
                      }}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-slate-900 px-4 py-2.5 text-xs font-bold text-white hover:bg-slate-700 transition-colors duration-300"
                    >
                      <Link2 size={13} />
                      {lang === "zh" ? "复制专属链接" : "Copy Link"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
